// Resistance, Immunity and Vulnerability — listed, editable, and applied in the
// order the rules give.
//
// The sheet's defences arrive as three strings through the effects lane
// (`Defenses` in effects.ts, as the statblock wrote them). They are read-only,
// they exist only for a token that represents a sheet, and they were applied on
// exactly one of the three damage paths — never on the initiative page, which is
// where combat is actually run. This module is the rest: what is in force on a
// row right now, what the DM adds or takes away, and what a hit comes to.
//
// THE ORDER MATTERS (SRD, Order of Application):
//
//   adjustments (bonuses, penalties, multipliers) → Resistance → Vulnerability
//
// Halving rounds down, so resistance and vulnerability on the same type do NOT
// simply cancel. 28 fire, in an aura that reduces all damage by 5: 23, halved to
// 11, doubled to 22. An earlier version of this file treated the pair as "cancels
// out" and asked the DM which to keep — a prompt for a question the rules have
// already answered, and one the DM could have answered wrongly.
//
// NO STACKING: two sources of resistance to the same type are one resistance.
// That falls out of `level` being a property rather than a count, but it is the
// reason an entry from a potion sitting beside an entry from the sheet is not a
// bug and must not halve twice.

/** The thirteen damage types, in the order the SRD lists them, plus the catch-all. */
export const DAMAGE_TYPES = [
  "acid", "bludgeoning", "cold", "fire", "force", "lightning", "necrotic",
  "piercing", "poison", "psychic", "radiant", "slashing", "thunder",
] as const;
export type DamageType = (typeof DAMAGE_TYPES)[number];
/** "Resistance to all damage" is a real entry (Stoneskin, a Rage, many monsters). */
export const ALL_DAMAGE = "all";

/** The three physical types, which are the ones that come with a qualifier. */
export const WEAPON_TYPES = ["bludgeoning", "piercing", "slashing"] as const;

export type DefenseLevel = "immune" | "resist" | "vulnerable";
export const DEFENSE_LEVELS: { value: DefenseLevel; label: string; short: string; hint: string }[] = [
  { value: "immune", label: "immune", short: "∅", hint: "No damage of this type at all." },
  { value: "resist", label: "resistant", short: "½", hint: "Halved, rounding down. Applied before any vulnerability." },
  { value: "vulnerable", label: "vulnerable", short: "×2", hint: "Doubled. Applied last, after any resistance has already halved it." },
];

/** Where a defence came from. Decides whether the DM may edit it and how it ends. */
export type DefenseSource = "sheet" | "spell" | "item" | "ruling";
export const DEFENSE_SOURCES: { value: DefenseSource; label: string; hint: string }[] = [
  { value: "sheet", label: "statblock", hint: "Read from the character or monster sheet. Remove it here to overrule the sheet for this fight." },
  { value: "spell", label: "spell", hint: "Granted by a spell or feature. Usually has a duration." },
  { value: "item", label: "item", hint: "A potion drunk, armour worn, a ring put on." },
  { value: "ruling", label: "your ruling", hint: "Something you decided at the table. It is standing in lava; it is soaked through." },
];

/**
 * One defence in force on one row.
 *
 * `nonmagicalOnly` is the SRD's commonest qualifier — "bludgeoning, piercing and
 * slashing from nonmagical attacks". It is a visible property of the entry rather
 * than something the damage path guesses at, because whether a given hit counts
 * as magical is often a judgement only the DM can make.
 */
export interface DefenseEntry {
  type: string;                 // a DamageType, or ALL_DAMAGE
  level: DefenseLevel;
  source: DefenseSource;
  /** "Potion of Fire Resistance", "Stoneskin", "standing in the brazier". */
  label?: string;
  nonmagicalOnly?: boolean;
  /** Rounds remaining. null / undefined: until something removes it. */
  rounds?: number | null;
  /** Stable id for editing and removal. */
  id: string;
}

/** What the DM has changed about a row, on top of the sheet. */
export interface RowDefenses {
  /** Added by the DM, by a spell, or by an item. */
  added: DefenseEntry[];
  /** Sheet entries the DM has struck out, as "<level>:<type>". */
  removed: string[];
}

export const EMPTY_ROW_DEFENSES: RowDefenses = { added: [], removed: [] };

const norm = (v: unknown) => String(v ?? "").trim().toLowerCase();
export const entryKey = (level: DefenseLevel, type: string) => `${level}:${norm(type)}`;
let seq = 0;
export const newDefenseId = () => `df${Date.now().toString(36)}${(seq++).toString(36)}`;

/**
 * Does a statblock clause cover this damage type?
 *
 * A clause is semicolon-separated, because "bludgeoning, piercing, and slashing
 * from nonmagical attacks" is ONE clause with a condition on the end and must not
 * be split on its commas into three unconditional entries.
 */
export function clauseCovers(list: string, dmgType: string): boolean {
  const type = norm(dmgType);
  if (!type) return false;
  return norm(list).split(";").some((clause) => {
    const c = clause.trim();
    if (!c) return false;
    const head = c.replace(/\bfrom\b.*$/, "").replace(/\bthat is not\b.*$/, "");
    if (head.trim() === ALL_DAMAGE || /^all\b/.test(head.trim())) return true;
    return head.split(/,|\band\b/).some((t) => {
      const w = t.trim();
      return w === type || w.startsWith(type + " ");
    });
  });
}

const clauseIsNonmagicalOnly = (list: string, dmgType: string): boolean =>
  norm(list).split(";").some((clause) => {
    const c = clause.trim();
    if (!c || !/non-?magical/.test(c)) return false;
    const head = c.replace(/\bfrom\b.*$/, "");
    return head.split(/,|\band\b/).some((t) => { const w = t.trim(); return w === norm(dmgType) || w.startsWith(norm(dmgType) + " "); });
  });

/** The sheet's three strings as listed entries, so the row can show and strike them. */
export function sheetEntries(
  sheet: { resist?: string; immune?: string; vulnerable?: string } | null | undefined,
): DefenseEntry[] {
  if (!sheet) return [];
  const out: DefenseEntry[] = [];
  const add = (list: string | undefined, level: DefenseLevel) => {
    if (!list) return;
    for (const type of [...DAMAGE_TYPES, ALL_DAMAGE]) {
      if (!clauseCovers(list, type)) continue;
      out.push({ id: `sheet:${level}:${type}`, type, level, source: "sheet", nonmagicalOnly: clauseIsNonmagicalOnly(list, type) || undefined });
    }
  };
  add(sheet.immune, "immune");
  add(sheet.resist, "resist");
  add(sheet.vulnerable, "vulnerable");
  return out;
}

/** Everything in force on a row: the sheet's, less what the DM struck, plus what was added. */
export function effectiveDefenses(
  sheet: { resist?: string; immune?: string; vulnerable?: string } | null | undefined,
  row: RowDefenses | undefined,
): DefenseEntry[] {
  const struck = new Set((row?.removed || []).map((k) => norm(k)));
  const fromSheet = sheetEntries(sheet).filter((d) => !struck.has(entryKey(d.level, d.type)));
  return [...fromSheet, ...(row?.added || [])];
}

/** Whether a hit of this type is affected, honouring "all damage" and the nonmagical qualifier. */
function applies(d: DefenseEntry, dmgType: string, magical: boolean | "unknown"): boolean | "unknown" {
  const t = norm(dmgType);
  if (norm(d.type) !== t && norm(d.type) !== ALL_DAMAGE) return false;
  if (!d.nonmagicalOnly) return true;
  if (magical === "unknown") return "unknown";
  return !magical;   // the clause applies only to nonmagical damage
}

export interface DamageBreakdown {
  /** What actually lands. */
  damage: number;
  /** Each step, for the row's tooltip and the activity log. */
  steps: string[];
  immune: boolean;
  resisted: boolean;
  doubled: boolean;
  /** Types whose nonmagical qualifier could not be decided from the source. */
  needsRuling: DefenseEntry[];
}

/**
 * Apply a hit, in the rules' order.
 *
 * `adjust` is the first step: flat bonuses and penalties, negative to reduce
 * (the aura in the SRD's own example). Then Resistance halves, rounding down.
 * Then Vulnerability doubles. Immunity short-circuits the lot.
 *
 * `magical` may be "unknown" — a number read off a chat card, say. An entry that
 * turns on the distinction is then reported in `needsRuling` and NOT applied, so
 * the row can ask once instead of the tool quietly picking a side.
 */
export function applyDamage(
  total: number,
  defenses: DefenseEntry[],
  dmgType: string | undefined,
  magical: boolean | "unknown" = "unknown",
  adjust = 0,
): DamageBreakdown {
  const steps: string[] = [];
  const needsRuling: DefenseEntry[] = [];
  if (!dmgType) {
    const flat = Math.max(0, total + adjust);
    if (adjust) steps.push(`${total} ${adjust < 0 ? "−" : "+"} ${Math.abs(adjust)} = ${flat}`);
    return { damage: flat, steps, immune: false, resisted: false, doubled: false, needsRuling };
  }

  const live = defenses.filter((d) => {
    const a = applies(d, dmgType, magical);
    if (a === "unknown") { needsRuling.push(d); return false; }
    return a;
  });
  const at = (level: DefenseLevel) => live.some((d) => d.level === level);

  if (at("immune")) {
    steps.push(`immune to ${norm(dmgType)}: no damage`);
    return { damage: 0, steps, immune: true, resisted: false, doubled: false, needsRuling };
  }

  let n = Math.max(0, total + adjust);
  if (adjust) steps.push(`${total} ${adjust < 0 ? "−" : "+"} ${Math.abs(adjust)} = ${n}`);

  // No stacking: several sources of resistance are one resistance.
  const resisted = at("resist");
  if (resisted) { const was = n; n = Math.floor(n / 2); steps.push(`resistant: ${was} halved to ${n}`); }
  const doubled = at("vulnerable");
  if (doubled) { const was = n; n = n * 2; steps.push(`vulnerable: ${was} doubled to ${n}`); }

  return { damage: n, steps, immune: false, resisted, doubled, needsRuling };
}

/**
 * What the picker offers.
 *
 * Every damage type plain, and for the three physical ones a SECOND option
 * qualified "from nonmagical attacks" — because both are real and they are not
 * the same defence. A monster resistant to slashing outright shrugs off a
 * +1 longsword; one resistant to nonmagical slashing does not, and that
 * difference is the whole reason a party buys magic weapons. Offering only the
 * qualified form would quietly make every such creature weaker than its
 * statblock; offering only the plain form would make it stronger.
 *
 * Plus two bundles, because they are how statblocks actually write it and
 * picking three separate entries for the commonest defence in the SRD would be
 * silly.
 */
export interface DefenseTypeOption { key: string; label: string; types: string[]; nonmagicalOnly?: boolean; group?: string }

export const DEFENSE_TYPE_OPTIONS: DefenseTypeOption[] = [
  { key: "all", label: "all damage", types: [ALL_DAMAGE], group: "everything" },
  { key: "bps", label: "bludgeoning, piercing & slashing", types: [...WEAPON_TYPES], group: "weapons" },
  { key: "bps-nonmagical", label: "bludgeoning, piercing & slashing — nonmagical only", types: [...WEAPON_TYPES], nonmagicalOnly: true, group: "weapons" },
  ...WEAPON_TYPES.flatMap((t) => [
    { key: t, label: t, types: [t], group: "weapons" },
    { key: `${t}-nonmagical`, label: `${t} — nonmagical only`, types: [t], nonmagicalOnly: true, group: "weapons" },
  ]),
  ...DAMAGE_TYPES.filter((t) => !(WEAPON_TYPES as readonly string[]).includes(t)).map((t) => ({ key: t, label: t, types: [t], group: "elemental & other" })),
];

/** A picked option as entries to store — the bundles become one entry per type. */
export function entriesFromOption(
  option: DefenseTypeOption,
  level: DefenseLevel,
  source: DefenseSource,
  extra?: { label?: string; rounds?: number | null },
): DefenseEntry[] {
  return option.types.map((type) => ({
    id: newDefenseId(),
    type,
    level,
    source,
    ...(option.nonmagicalOnly ? { nonmagicalOnly: true } : {}),
    ...(extra?.label ? { label: extra.label } : {}),
    ...(extra?.rounds !== undefined ? { rounds: extra.rounds } : {}),
  }));
}

/** The compact strip a row shows: "∅ poison · ½ b/p/s* · ×2 cold". */
export function describeDefenses(defenses: DefenseEntry[], limit = 4): { text: string; more: number } {
  const order: DefenseLevel[] = ["immune", "resist", "vulnerable"];
  const short = (l: DefenseLevel) => DEFENSE_LEVELS.find((x) => x.value === l)!.short;
  const key = (d: DefenseEntry) => `${d.level}:${d.nonmagicalOnly ? "n" : "m"}`;

  /* bludgeoning, piercing and slashing at the same level and qualifier collapse
     to "b/p/s". It is the commonest defence in the SRD and spelling it out fills
     the row three times over, which defeats the point of having the row say
     anything at a glance. */
  const rest: DefenseEntry[] = [];
  const bundles: { level: DefenseLevel; nonmagicalOnly?: boolean }[] = [];
  const byKey = new Map<string, DefenseEntry[]>();
  for (const d of defenses) {
    if ((WEAPON_TYPES as readonly string[]).includes(norm(d.type))) {
      const k = key(d);
      byKey.set(k, [...(byKey.get(k) || []), d]);
    } else rest.push(d);
  }
  for (const [, group] of byKey) {
    const types = new Set(group.map((d) => norm(d.type)));
    if (types.size === WEAPON_TYPES.length) bundles.push({ level: group[0].level, nonmagicalOnly: group[0].nonmagicalOnly });
    else rest.push(...group);
  }

  const parts = [
    ...bundles.map((b) => ({ level: b.level, text: `${short(b.level)} b/p/s${b.nonmagicalOnly ? "*" : ""}`, sort: "b/p/s" })),
    ...rest.map((d) => ({ level: d.level, text: `${short(d.level)} ${d.type}${d.nonmagicalOnly ? "*" : ""}`, sort: norm(d.type) })),
  ].sort((a, b) => order.indexOf(a.level) - order.indexOf(b.level) || a.sort.localeCompare(b.sort));

  return { text: parts.slice(0, limit).map((p) => p.text).join(" · "), more: Math.max(0, parts.length - limit) };
}

/** One line per defence, for the tooltip: what it is, where it came from, how long. */
export const describeEntry = (d: DefenseEntry): string =>
  [
    `${DEFENSE_LEVELS.find((x) => x.value === d.level)!.label} to ${d.type}`,
    d.nonmagicalOnly ? "from nonmagical attacks" : "",
    d.label ? `(${d.label})` : d.source === "sheet" ? "(statblock)" : "",
    d.rounds ? `— ${d.rounds} round${d.rounds === 1 ? "" : "s"} left` : "",
  ].filter(Boolean).join(" ");

/**
 * Count a row's timed defences down by one round, dropping those that expire.
 *
 * Deliberately the same shape as the condition clock, so a potion of fire
 * resistance and a condition with a duration behave alike and a DM does not have
 * to learn two models. Entries with no `rounds` are untouched.
 */
export function tickDefenses(row: RowDefenses | undefined): { next: RowDefenses | null; expired: DefenseEntry[] } {
  const added = row?.added || [];
  if (!added.some((d) => typeof d.rounds === "number")) return { next: null, expired: [] };
  const expired: DefenseEntry[] = [];
  const kept: DefenseEntry[] = [];
  for (const d of added) {
    if (typeof d.rounds !== "number") { kept.push(d); continue; }
    const left = d.rounds - 1;
    if (left <= 0) expired.push(d); else kept.push({ ...d, rounds: left });
  }
  if (!expired.length) return { next: { ...(row as RowDefenses), added: kept }, expired: [] };
  return { next: { ...(row as RowDefenses), added: kept }, expired };
}
